import { SOCIAL_NETWORK_LABEL, } from '@wit-support/admin-contracts';
/**
 * Имя сети для фраз: имя от движка (`networkLabel` ячейки), иначе словарь
 * контрактов; сеть не известна — «Instagram» (прежние тексты).
 */
export function networkName(network, label) {
    if (label)
        return label;
    return network ? SOCIAL_NETWORK_LABEL[network] : SOCIAL_NETWORK_LABEL.instagram;
}
/**
 * Фраза Instagram → та же фраза для сети: «Instagram» заменяется именем сети.
 * У Instagram (и без сети) — строка как есть. Для фраз, где у Facebook другой
 * смысл (где проверять ограничение, какие права), — свои тексты, не замена.
 */
export function netText(text, network, label) {
    if (!network || network === 'instagram')
        return text;
    const named = text.replaceAll('Instagram', networkName(network, label));
    // У Facebook ограничивают Страницу, а не «аккаунт» (запасные фразы без текста движка).
    return network === 'facebook'
        ? named
            .replaceAll('ограничил действия аккаунта', 'ограничил действия Страницы')
            .replaceAll('ограничил аккаунт', 'ограничил Страницу')
        : named;
}
/** Своё значение у Facebook, у остальных — Instagram-вариант с заменой имени сети. */
export function byNetwork(network, instagram, facebook) {
    return network === 'facebook' ? facebook : netText(instagram, network);
}
// ---------------------------------------------------------------------------
// Доступы системного пользователя на Странице Facebook (FB-SPEC §3.2, L7)
// ---------------------------------------------------------------------------
/**
 * Задачи Страницы (`pageTasks` движка) — словами из Business Settings
 * (переключатели частичного доступа системного пользователя, RV-FB-SPEC L7).
 */
export const PAGE_TASK_LABEL_RU = {
    CREATE_CONTENT: 'Контент',
    MODERATE: 'Активность сообщества',
    MESSAGING: 'Сообщения',
    ADVERTISE: 'Реклама',
    ANALYZE: 'Статистика',
    MANAGE: 'Полный доступ',
};
/** Нет «Активность сообщества» (MODERATE): ответ, скрытие, удаление недоступны. */
export const NO_MODERATE_TEXT_RU = 'нет доступа «Активность сообщества» — ответы, скрытие и удаление недоступны';
/** Нет «Контент» (CREATE_CONTENT): сердечко недоступно. */
export const NO_CREATE_CONTENT_TEXT_RU = 'нет доступа «Контент» — сердечки недоступны';
/** Сеть не сообщила задачи (`pageTasks: null`) — умения не режутся, проверит сеть. */
export const PAGE_TASKS_UNKNOWN_TEXT_RU = 'доступы на Странице сеть не сообщила — проверятся при первом действии';
/**
 * Доступы системного пользователя на Странице словами. «Полный доступ»
 * (MANAGE) покрывает оба нужных; нужны «Контент» (сердечки) и «Активность
 * сообщества» (ответ, скрыть, удалить). `null` — сеть задачи не отдала:
 * пометок нет (умения не режутся, FB-SPEC §3.2), только пояснение.
 */
export function pageAccess(tasks) {
    if (tasks === null)
        return { text: null, warnings: [] };
    if (tasks.includes('MANAGE')) {
        return { text: `доступ: ${PAGE_TASK_LABEL_RU.MANAGE} ✓`, warnings: [] };
    }
    const content = tasks.includes('CREATE_CONTENT');
    const moderate = tasks.includes('MODERATE');
    const mark = (ok) => (ok ? '✓' : '✗');
    return {
        text: `доступы: ${PAGE_TASK_LABEL_RU.CREATE_CONTENT} ${mark(content)}, ${PAGE_TASK_LABEL_RU.MODERATE} ${mark(moderate)}`,
        warnings: [
            ...(moderate ? [] : [NO_MODERATE_TEXT_RU]),
            ...(content ? [] : [NO_CREATE_CONTENT_TEXT_RU]),
        ],
    };
}
/** Токен Страницы (им идут все действия на Facebook): получен или нет. */
export const PAGE_TOKEN_OK_TEXT_RU = 'токен Страницы получен';
export const PAGE_TOKEN_MISSING_TEXT_RU = 'токена Страницы нет — ответы, сердечки, скрытие и удаление не пойдут';
// ---------------------------------------------------------------------------
// «Подключить тем же токеном, что у Instagram» (FB-SPEC §0.4, §6.5)
// ---------------------------------------------------------------------------
export const REUSE_LABEL_RU = 'Подключить тем же токеном, что у Instagram';
export const REUSE_SOURCE_MISSING_TEXT_RU = 'У Instagram этого аккаунта нет рабочего токена Facebook — сначала подключите его в строке Instagram или вставьте токен вручную';
/** Что произойдёт после «Подключить тем же токеном» — текст подтверждения. */
export function reuseConfirmText(account) {
    return `Движок возьмёт токен из подключения «Instagram через Facebook» аккаунта ${account}, сам найдёт Страницу Facebook этого Instagram и проверит токен. Вставлять токен не нужно, увидеть его нельзя. Читать комментарии Страница начнёт сразу; для ответов, сердечек, скрытия и удаления у токена должно быть право pages_manage_engagement, а у системного пользователя на Странице — доступы «Контент» и «Активность сообщества». Режим реагирования Страницы это не меняет: у новой пары он «Выключено», пока его не сменят в «Настройках реагирования», а у уже настроенной остаётся прежним.`;
}
// ---------------------------------------------------------------------------
// Комментарий Facebook: автор без username, отметки, вложения
// ---------------------------------------------------------------------------
/**
 * Автор комментария: у Facebook — имя (`authorName`, username там нет), у
 * Instagram — `@username`. Нет ни того, ни другого — null (экран пишет
 * «автор скрыт»).
 */
export function commentAuthorLabel(c) {
    if (c.authorName)
        return c.authorName;
    return c.authorUsername ? `@${c.authorUsername}` : null;
}
/** Сколько имён отметок показывать в бейдже; остальные — «и ещё N». */
const TAGS_SHOWN = 2;
/** «отметил: Maria Silva, Juan Torres и ещё 3» — отметки людей (Facebook `message_tags`). */
export function tagsText(tags) {
    if (tags.length === 0)
        return null;
    const names = tags.slice(0, TAGS_SHOWN).map((t) => t.name);
    const rest = tags.length - names.length;
    return `отметил: ${names.join(', ')}${rest > 0 ? ` и ещё ${rest}` : ''}`;
}
/** Бейдж вложения: стикер, GIF или фото в комментарии. */
export const ATTACHMENT_MARK_RU = 'стикер/GIF';
/** Комментарий без текста, только вложение. */
export const ATTACHMENT_ONLY_TEXT_RU = '(без текста — стикер или GIF)';
// ---------------------------------------------------------------------------
// Лимиты сети (FB-SPEC §4.2): у Facebook ниже, чем у Instagram
// ---------------------------------------------------------------------------
const LIMIT_SHORT_RU = {
    reply: 'ответы',
    like: 'сердечки',
    hide: 'скрытие',
    delete: 'удаление',
    total: 'все действия вместе',
};
const LIMIT_ORDER = ['reply', 'like', 'hide', 'delete', 'total'];
function capText(kind, c) {
    return `${LIMIT_SHORT_RU[kind]} — до ${c.perHour} в час${c.per24h !== null ? ` и ${c.per24h} за сутки` : ''}`;
}
/** Профиль защиты сети из `safetyByNetwork` ответа движка; нет — undefined. */
export function networkSafetyOf(byNetwork, network) {
    return byNetwork?.[network] ?? undefined;
}
/**
 * Подсказка о лимитах сети, у которой они свои (сейчас — Facebook): «Лимиты
 * Facebook ниже, чем у Instagram…» + потолки, лимит на один пост и паузы
 * между ответами — из профиля движка. У Instagram — null (подсказки не было).
 */
export function networkLimitsHint(network, profile) {
    if (network !== 'facebook')
        return null;
    const parts = [
        'Лимиты Facebook ниже, чем у Instagram: Страница — рекламный актив, и её ограничение может задеть рекламу.',
    ];
    const caps = profile?.caps ?? null;
    if (caps) {
        const list = LIMIT_ORDER.flatMap((k) => {
            const c = caps[k];
            return c ? [capText(k, c)] : [];
        });
        if (list.length > 0)
            parts.push(`Потолки: ${list.join('; ')}.`);
    }
    const post = profile?.postLimitsPerHour ?? {};
    if (post.like !== undefined || post.reply !== undefined) {
        const p = [
            ...(post.like !== undefined ? [`${post.like} сердечек`] : []),
            ...(post.reply !== undefined ? [`${post.reply} ответов`] : []),
        ];
        parts.push(`Под одним роликом за час — не больше ${p.join(' и ')}.`);
    }
    const gap = profile?.intervalsSec.reply;
    if (gap)
        parts.push(`Пауза между ответами — от ${gap.minSec} до ${gap.maxSec} секунд.`);
    return parts.join(' ');
}
// ---------------------------------------------------------------------------
// Пары одного аккаунта (связка сетей, FB-SPEC §4.2 M3)
// ---------------------------------------------------------------------------
const SIBLING_REASONS = new Set(['blocked_sibling', 'spam_sibling']);
const BLOCK_REASONS = new Set(['blocked_368', 'blocked_repeat']);
/**
 * Сеть-источник паузы «из-за другой сети» (`blocked_sibling` / `spam_sibling`)
 * для своей фразы, если движок не прислал готовую: другая сеть того же
 * аккаунта, у которой сейчас ограничение (368) или пауза ответов. Кандидат
 * один (у аккаунта Instagram и Facebook) — он. Не нашли — null (фраза без
 * имени второй сети).
 */
export function siblingNetworkOf(cell, items, reason) {
    // Движок сам называет сеть-источник (`safety.siblingNetwork`); запасной путь —
    // вычисление ниже (старый движок поле не шлёт).
    const given = cell.safety?.siblingNetwork ?? null;
    if (given !== null && given !== cell.network && reason !== null && SIBLING_REASONS.has(reason)) {
        return given;
    }
    const others = items.filter((c) => c.account === cell.account && c.network !== cell.network && c.supported);
    const signal = others.find((c) => {
        const s = c.safety;
        if (!s)
            return false;
        if (reason === 'spam_sibling')
            return s.replyPausedUntil !== null;
        return s.blockedAt !== null || (s.pauseReason !== null && BLOCK_REASONS.has(s.pauseReason));
    });
    if (signal)
        return signal.network;
    return others.length === 1 ? (others[0]?.network ?? null) : null;
}
//# sourceMappingURL=network-text.js.map